import { useState, useEffect, useRef, useContext } from 'react';
import { UIContext } from '../../contexts/UIContext';

import { Button } from '@mui/material';

import LogUtils from '../../utils/LogUtils';
import {
  isMapInstanceGoogleMaps,
  loadPins,
  createUserLocationPin,
  createHeatmap,
  loadSpecificPolygon,
  loadPolygons,
  mapStyle,
  addEventListenersCreatePolygon,
} from '../../utils/MapUtils';

import './SimpleMap.scss';

function SimpleMap({
  className, center, focusedGeoLocation, geoLocationToLoad, isEditEnabled, mapTypeId, refreshMap, readOnly, zoom,
  showWorkingAreas, workingAreas,
  showNeighborhoods, neighborhoods,
  showPins, pins, pinsFilters, handleClickVectorFocus,
  showHeatmap, heatmapData,
  showSpecificPolygon, onPolygonClicked, onPolygonComplete, cancelOperation,
  showDrawingManager,
  showUserLocation, userLocation
}) {
  const {showWarningMessage} = useContext(UIContext);

  const [isMounted, setIsMounted] = useState(true);
  const [updateComponent, setUpdateComponent] = useState(refreshMap || 0);
  const [map, setMap] = useState(null);
  const [selectedShape, setSelectedShape] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [markersClusterer, setMarkersClusterer] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [drawingManager, setDrawingManager] = useState(null);
  const [workingAreasPolygons, setWorkingAreasPolygons] = useState([]);
  const [neighborhoodsPolygons, setNeighborhoodsPolygons] = useState([]);

  const TAG = "SimpleMap";
  const classNames = `simple-map ${className || ''}`;
  const logUtils = new LogUtils();
  const userLocationMarker = useRef();
  let newShape = null;

  // Lifecycle
  useEffect(() => {
    setIsMounted(true);
    return (() => {
      setIsMounted(false);
    });
  }, []);

  useEffect(() => {
    if (window.google && center && (!map || refreshMap !== updateComponent)) {
      setUpdateComponent(refreshMap);
      setDrawingManager(null);
      setMap(new window.google.maps.Map(document.getElementById("map"), {
        mapTypeId: mapTypeId,
        center: {
          lat: Number(center.lat),
          lng: Number(center.lng)
        },
        zoom: zoom,
        zoomControl: false
      }));
    }
  }, [map, updateComponent, refreshMap, zoom, center, mapTypeId]);

  useEffect(() => {
    updateUserLocation(userLocation);
  }, [map, showUserLocation, userLocation])

  useEffect(() => {
    updateWorkingAreasPolygons();
  }, [map, showWorkingAreas, workingAreas, geoLocationToLoad, focusedGeoLocation]);

  useEffect(() => {
    updateNeighborhoodsPolygons();
  }, [map, showNeighborhoods, neighborhoods, geoLocationToLoad, focusedGeoLocation]);

  useEffect(() => {
    updateMarkers();
  }, [map, showPins, pins, pinsFilters]);

  useEffect(() => {
    updateHeatmap();
  }, [map, showHeatmap]);

  useEffect(() => {
    updateSpecificPolygon();
  }, [map, showSpecificPolygon, geoLocationToLoad, readOnly, isEditEnabled, geoLocationToLoad]);

  useEffect(() => {
    updateDrawingManager();
  }, [map, isMounted, refreshMap, isEditEnabled, readOnly, geoLocationToLoad, showWorkingAreas, workingAreas]);

  // Initialization
  function updateUserLocation(userLocation) {
    userLocationMarker.current?.setMap(null);

    if (!showUserLocation) return;

    userLocationMarker.current = createUserLocationPin({ map, userPosition: userLocation })
    userLocationMarker.current?.setMap(map);
  };

  function updateMarkers() {
    while (markers?.length) {
      markers.pop().setMap(null);
    }
    markersClusterer?.forEach((clusterer) => {
      clusterer.clearMarkers();
    });

    if (!showPins) return;

    loadPins({
      map,
      pins,
      markers,
      markersClusterer,
      setMarkers,
      setMarkersClusterer,
      handleClickMarkerInfo: handleClickVectorFocus
    });
  }

  function updateHeatmap() {
    heatmap?.setMap?.(null);

    if (!showHeatmap || !heatmapData) return;

    const newHeatmap = createHeatmap(map, heatmapData);
    newHeatmap?.setMap?.(map);
    setHeatmap(newHeatmap);
  }

  function updateWorkingAreasPolygons() {
    while (workingAreasPolygons?.length) {
      workingAreasPolygons.pop().setMap(null);
    }

    if (!showWorkingAreas) return;

    updateListPolygons({
      showPolygons: showWorkingAreas,
      readOnly: readOnly,
      isEditEnabled: isEditEnabled,
      geoLocationToLoad: geoLocationToLoad,
      focusedGeoLocation: focusedGeoLocation,
      listPolygons: workingAreas,
      callbackLoadPolygons: loadWorkingAreas,
      callbackCleanPolygons: () => {
        while (workingAreasPolygons.length) {
          workingAreasPolygons.pop().setMap(null);
        }
      }
    });
  }

  function updateNeighborhoodsPolygons() {
    updateListPolygons({
      showPolygons: showNeighborhoods,
      readOnly: readOnly,
      isEditEnabled: isEditEnabled,
      geoLocationToLoad: geoLocationToLoad,
      focusedGeoLocation: focusedGeoLocation,
      listPolygons: neighborhoods,
      callbackLoadPolygons: loadNeighborhoods,
      callbackCleanPolygons: () => {
        while (neighborhoodsPolygons.length) {
          neighborhoodsPolygons.pop().setMap(null);
        }
      }
    });
  }

  function updateListPolygons({ showPolygons, readOnly, isEditEnabled, geoLocationToLoad,
    focusedGeoLocation, listPolygons, callbackLoadPolygons, callbackCleanPolygons }) {
    if (!isMapInstanceGoogleMaps(map)) return;

    if (showPolygons) {
      if (readOnly || isEditEnabled) {
        callbackLoadPolygons(map, geoLocationToLoad, listPolygons, focusedGeoLocation);
      } else {
        callbackLoadPolygons(map, null, listPolygons);
      }
    } else if (callbackCleanPolygons) {
      callbackCleanPolygons();
    }
  }

  function updateSpecificPolygon() {
    if (showSpecificPolygon && geoLocationToLoad) {
      loadSpecificPolygon(readOnly, isEditEnabled, map, geoLocationToLoad, selectedShape, setSelectedShape);
    }
  }

  function updateDrawingManager() {
    try {
      if (showDrawingManager && isEditEnabled) {
        initDrawingManager(map);
      }
    } catch (err) {
      logUtils.logErrorMsg(TAG, "Unable to updateDrawingManager", err);
    }
  }

  function initDrawingManager(map) {
    try {
      if (!map || drawingManager) {
        return false;
      }
      const manager = new window.google.maps.drawing.DrawingManager({
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.LEFT_BOTTOM,
          drawingModes: ['polygon']
        },
        polygonOptions: {
          fillColor: mapStyle.polygons.fill,
          fillOpacity: 0.25,
          strokeWeight: .5,
          clickable: true,
          editable: true,
          draggable: true,
          zIndex: 1
        }
      });
      manager.setMap(map);

      addEventListenersCreatePolygon(manager, setSelectedShape, newShape);
      setDrawingManager(manager);

    } catch (err) {
      logUtils.logErrorMsg(TAG, "Unable to initDrawingManager", err);
    }
  }

  function loadWorkingAreas(map, geoLocationToLoad, workingAreas, focusedGeoLocation) {
    try {
      const loadedPolygons = loadPolygons({
        map,
        geoLocationToLoad,
        focusedGeoLocation,
        polygons: workingAreas,
        reactStatePolygons: workingAreasPolygons,
        useGeojsonBounds: false,
        onPolygonClicked: onPolygonClicked,
        callbackAddPolygon: (newPolygon) => {
          const updatedPolygons = workingAreasPolygons;
          updatedPolygons.push(newPolygon);
          setWorkingAreasPolygons(updatedPolygons);
        }
      });
      if (loadedPolygons?.length === 0) {
        showWarningMessage({
          message: "Não foi possível carregar as áreas de trabalho para o local selecionado. Possivelmente esta cidade ainda não foi cadastrada no app."
        });
      }
    } catch (err) {
      logUtils.logErrorMsg(TAG, "Unable to loadWorkingAreas", err);
    }
    return false;
  }

  function loadNeighborhoods(map, geoLocationToLoad, neighborhoods, focusedGeoLocation) {
    try {
      const loadedPolygons = loadPolygons({
        map,
        geoLocationToLoad,
        focusedGeoLocation,
        polygons: neighborhoods,
        reactStatePolygons: neighborhoodsPolygons,
        useGeojsonBounds: true,
        onPolygonClicked: onPolygonClicked,
        callbackAddPolygon: (newPolygon) => {
          const updatedPolygons = neighborhoodsPolygons;
          updatedPolygons.push(newPolygon);
          setNeighborhoodsPolygons(updatedPolygons);
        }
      });
      if (loadedPolygons?.length === 0) {
        showWarningMessage({
          message: "Não foi possível carregar os bairros para o local selecionado. Possivelmente esta cidade ainda não foi cadastrada no app."
        });
      }
    } catch (err) {
      logUtils.logErrorMsg(TAG, "Unable to loadNeighborhoods", err);
    }
    return false;
  }

  // Actions
  function updatedPickedGeoLocation() {
    onPolygonComplete?.(selectedShape);
  }

  // Rendering
  return (
    isMounted ? (
      <div className={classNames}>
        <div
          id="map"
          className="map">
        </div>
        {
          readOnly ? "" : (
            <div className="action-btns-container">
              <Button className="confirm-coordinates-btn" onClick={updatedPickedGeoLocation}>
                Atualizar localização
              </Button>
              {
                !readOnly && isEditEnabled ? (
                  <Button className="cancel-coordinates-btn" onClick={cancelOperation}>
                    Cancelar
                  </Button>
                ) : ""
              }
            </div>
          )
        }
      </div>
    ) : ("")
  );
}

export default SimpleMap;
